import { isBrowser } from './device-type';
import * as sessionStorage from './session-storage';
import URLX, { currentUrl, isInternalUrl } from './Url';
import { scopeSiteContainerAttribute } from './rendering/browser';
import { scrollPosition, scrollTo, offsetTop } from './Root';

export const scopeLinkAttribute = 'data-scope-link';
export const scopeLinksContainerAttribute = 'data-scope-links-container';

let hashScrollOffset = 100;

type LegacyUrl = {
  readonly path: string;
  readonly query: { [key: string]: any };
  readonly hash: string;
};

export function convertLegacyUrlToString(url: LegacyUrl) {
  const search = url.query
    ? Object.keys(url.query)
        .map(
          key =>
            Array.isArray(url.query[key])
              ? (url.query[key] as any[])
                  .map(value => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                  .join('&')
              : `${encodeURIComponent(key)}=${encodeURIComponent(url.query[key])}`,
        )
        .join('&')
    : '';
  return new URLX(`${url.path}${search ? '?' + search : ''}${url.hash}`);
}

type HistoryState = {
  url: string | LegacyUrl;
  // An unique id to identify a state object
  stateId: string;
  // The estimated index for this state objects position in the browser history.
  // That is, the object with index 0 is the oldest and first state in the history.
  // And the object with index `history.length - 1` is the newest. This number is
  // incremented from the current state, so the process could look like:
  // 1. User visits / - state object with index 0
  // 2. User visits /smink - state object with index 1
  // 3. User visits /smink/hudtyp - state object with index 2
  // 4. User hits back twice to get to the start page
  // 5. User visits /harvard - state object with index 1 and the states for /smink and /smink/hudtyp are gone
  stateIndex: number;
};

export type HistoryEvent =
  | 'push'
  | 'pop'
  | 'replace'
  | 'push-loaded'
  | 'push-failed'
  | 'pop-loaded'
  | 'pop-failed'
  | 'replace-loaded'
  | 'replace-failed';

// These types are used when the user initiates a state change but we are unable
// to load the page. The process looks like this:
// 1. User visits /
// 2. User clicks on a link to /smink
// 3. The address bar now shows /smink, even if we're still on the start page with a loading spinner
// 4. The page /smink fails to load
// 5. The state change is reverted so the address bar shows / again
// 6. The user is presented with an error with a Retry-button
// 7. If she clicks on the Retry button, we go back to point 3.
type RevertStateChange = () => RetryStateChange;
type RetryStateChange = () => boolean;

export type HistoryOptions = {
  merge?: (a: any, b: any) => any;
  linkIdentifier?: string;
  skipPartialCache?: boolean;
  hideSpinner?: boolean;
  includeAppShellData?: boolean;
  resolveDynamicData?: boolean;
  skipReloadOnVersionMismatch?: boolean;
  deferLoadPromise?: Promise<any> | null;
  scrollPosition?: number;
  skipLoad?: boolean;
};

export type HistoryEventObject = {
  url: URLX;
  event: HistoryEvent;
  state: HistoryState;
  previousState: HistoryState;
  options: HistoryOptions;
  revert: RevertStateChange;
  scrollPosition: number | undefined;
};

type HistoryEventCallback = (event: HistoryEventObject) => void | Promise<any>;

let listeners: Array<{ event: HistoryEvent; callback: HistoryEventCallback }> = [];

export const historyAPISupported = (() => {
  if (!isBrowser()) {
    return false;
  }
  const ua = navigator.userAgent;
  if (
    (ua.indexOf('Android 2.') !== -1 || ua.indexOf('Android 4.0') !== -1) &&
    ua.indexOf('Mobile Safari') !== -1 &&
    ua.indexOf('Chrome') === -1 &&
    ua.indexOf('Windows Phone') === -1
  ) {
    return false;
  }

  // Don't use History API for edit mode, this to make on page editing work
  if (window.IS_IN_EDIT_MODE) {
    return false;
  }

  return window.history && 'pushState' in window.history;
})();

// There's a difference between `currentState` and `window.history.state` in that
// when a `popstate` event is fired, `window.history.state` is already the state poped
// to and we need to know what the state was before that event.
let currentState: HistoryState = (() => {
  if (isBrowser()) {
    if (window.history.state && window.history.state.stateId) {
      const state = window.history.state;
      // `stateIndex` didn't always exist
      state.stateIndex = getStateIndex(state);
      return state;
    } else {
      const url = currentUrl().href;
      const state: HistoryState = {
        url,
        stateId: stateId(),
        stateIndex: 0,
      };
      if (historyAPISupported) {
        window.history.replaceState(state, '', url);
      }
      return state;
    }
  }
  return {
    url: '',
    stateId: '',
    stateIndex: 0,
  };
})();

let ignoreNextPopStateEvent = false;

export function pushState(url?: string | URLX | null, options: HistoryOptions = {}) {
  if (!url) {
    url = currentUrl();
  } else if (typeof url === 'string') {
    url = new URLX(url);
  } else if (!(url instanceof URLX)) {
    throw new Error('Unknown type for `url` passed to pushState: ' + typeof url + ' ' + JSON.stringify(url));
  }

  const href = url.href;

  if (historyAPISupported) {
    const previousState = currentState;
    saveScrollPosition(previousState, scrollPosition());

    const state = {
      stateId: stateId(),
      stateIndex: getStateIndex(previousState) + 1,
      // We clear `hiddenParam` here because when we get a `popstate` event
      // we don't want the `hiddenParam`. The hidden query string is used
      // to pass information to the server to only load a partial page
      // and if we go back/forward to that state, we want the full page.
      url: href,
    };

    window.history.pushState(state, '', href);
    currentState = state;
    return triggerEvent({
      event: 'push',
      url,
      state,
      previousState,
      scrollPosition: 0,
      options,
      revert() {
        // Note that we pass `skipPopEvent: true` here.
        // This because we only want to revert the address bar change and not application
        // state. The application state has already been reverted here.
        backHistory(true);

        return () => {
          console.debug('Retrying reverted push state change to', href);
          pushState(url, options);
          return true;
        };
      },
    });
  } else {
    window.location.href = href;
  }
}

export function replaceState(url?: string | URLX | null, options: HistoryOptions = {}, dispatchEvent = true) {
  if (!url) {
    url = currentUrl();
  } else if (typeof url === 'string') {
    url = new URLX(url);
  } else if (!(url instanceof URLX)) {
    throw new Error('Unknown type for `url` passed to replaceState: ' + typeof url + ' ' + JSON.stringify(url));
  }

  const href = url.href;

  const previousState = currentState;
  const state = {
    stateId: previousState.stateId,
    stateIndex: getStateIndex(previousState),
    // We clear `hiddenParam` for the same reason as we do it in `pushState`
    url: href,
  };

  // If the browser doesn't support the history API, we continue on but without
  // syncing it to the address bar. We could make a full page load here, but `replaceState`
  // is often used for things where it's not crucial for the url to update and where a page
  // load would give a quite poor user experience.
  if (historyAPISupported) {
    window.history.replaceState(state, '', href);
  }
  currentState = state;

  // If `replaceState` is called with `dispatchEvent` === false it means that the url bar
  // is not in sync with what the server gave us (the server redirected). So we call replaceState
  // to get back in sync but we don't want to dispatch any events because that would cause us to
  // reload the page which we don't need.
  if (!dispatchEvent) {
    return;
  }
  return triggerEvent({
    event: 'replace',
    url,
    state,
    scrollPosition: scrollPosition(),
    previousState,
    options,
    revert() {
      if (historyAPISupported) {
        // Note that we call `history.replaceState` and not our own `replaceState` here.
        // This because we only want to revert the address bar change and not application
        // state. The application state has already been reverted here.
        window.history.replaceState(
          previousState,
          '',
          typeof previousState.url === 'string' ? previousState.url : convertLegacyUrlToString(previousState.url).href,
        );
      }

      return () => {
        console.debug('Retrying reverted replace state change to', href);
        replaceState(url, options);
        return true;
      };
    },
  });
}

export function onHistory(events: HistoryEvent[] | HistoryEvent, callback: HistoryEventCallback) {
  const eventList: HistoryEvent[] = typeof events === 'string' ? [events] : events;
  eventList.forEach(eventName => listeners.push({ event: eventName, callback }));

  // We return a function here that you can call to unscribe to the event.
  // This instead of exporting an `off` function where you have to pass
  // the same function you passed to `on` which makes it impossible to
  // pass an inline function to `on`.
  return () =>
    eventList.forEach(
      eventName => (listeners = listeners.filter(l => !(l.callback === callback && l.event === eventName))),
    );
}

export function oneHistory(event: HistoryEvent[] | HistoryEvent, callback: HistoryEventCallback) {
  const unsubscribe = onHistory(event, e => {
    callback(e);
    unsubscribe();
  });
}

export function backHistory(skipPopEvent = false) {
  ignoreNextPopStateEvent = skipPopEvent;
  window.history.back();
}

export function forwardHistory(skipPopEvent = false) {
  ignoreNextPopStateEvent = skipPopEvent;
  window.history.forward();
}

export function goHistory(index: number, skipPopEvent = false) {
  ignoreNextPopStateEvent = skipPopEvent;
  window.history.go(index);
}

export function setHashScrollOffset(offset: number) {
  hashScrollOffset = offset;
}

export function scrollToHash(hash: string, animate = true) {
  let anchorElement = document.getElementById(hash.substr(1));
  if (!anchorElement) {
    // We might have removed the ID to prevent the browser to do it's own scroll
    // In that case, we need to check if it exists in data-scroll-id
    anchorElement = document.querySelector('*[data-scroll-id=' + hash.substr(1) + ']');
  }
  if (anchorElement) {
    const offset = offsetTop(anchorElement as HTMLElement) - hashScrollOffset;
    if (animate) {
      scrollTo(offset, 500);
    } else {
      scrollPosition(offset);
    }
    return true;
  } else {
    return false;
  }
}

export function scrollToHashAfterInitialRender(hash: string) {
  const id = hash.substr(1);
  const anchorElement = document.getElementById(id);
  if (anchorElement) {
    // We need to remove the id to prevent the browser to do it's own scroll
    // We also store the removed ID to a custom attribute so we still can scroll to it
    
    anchorElement.setAttribute('data-scroll-id', id);
    anchorElement.removeAttribute('id');
    location.hash = '';
    // If it's from the initial render we need to wait for the layout to "settle down"
    // after all images have been loaded
    const images = document.getElementsByTagName('img');
    const promises: Array<Promise<any>> = [];
    // tslint:disable-next-line prefer-for-of
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      promises.push(
        new Promise(resolve => {
          if (image.complete) {
            resolve();
          } else {
            image.onload = resolve;
            image.onerror = resolve;
          }
        }),
      );
    }

    return Promise.all(promises).then(() => {
      const offset = offsetTop(anchorElement as HTMLElement) - hashScrollOffset;
      scrollPosition(offset);

      if (id) {
        location.hash = id;
      }

      // This is needed because otherwise some browsers will scroll to the element.
      // Which is fine for some cases but we want to add our offset to the scroll
      // position. If an element exist with the specified id in the onload event
      // some browsers will scroll to it. By waiting a bit before we reapply the
      // id we circumvent the browsers scroll.
      const currentOnload = window.onload;
      window.onload = (e: Event) => {
        if (currentOnload) {
          currentOnload.call(window, e);
        }
        if (id) {
          setTimeout(() => anchorElement.setAttribute('id', id), 0);
        }
      };
      return true;
    });
  } else {
    return Promise.resolve(false);
  }
}

if (process.env.NODE_ENV !== 'production') {
  if (isBrowser() && !window.IS_IN_EDIT_MODE) {
    const parentHasAttribute = (link: HTMLAnchorElement, attributeName: string) => {
      let current: HTMLElement | null = link;
      while (current) {
        if (current.hasAttribute(attributeName)) {
          return true;
        }
        current = current.parentElement;
      }
      return false;
    };

    const hasEpiWidgetAsParent = (link: HTMLAnchorElement) => {
      let current: HTMLElement | null = link;
      while (current) {
        if (current.getAttribute('id') === 'epi-quickNavigator') {
          return true;
        }
        current = current.parentElement;
      }
      return false;
    };

    if (process.env.NODE_ENV !== 'production') {
      document.addEventListener(
        'click',
        e => {
          const link = clickedLink(e);
          if (
            link &&
            !isExternalLink(link) &&
            !isHashLink(link) &&
            !link.hasAttribute(scopeLinkAttribute) &&
            !parentHasAttribute(link, scopeLinksContainerAttribute) &&
            parentHasAttribute(link, scopeSiteContainerAttribute) &&
            !hasEpiWidgetAsParent(link)
          ) {
            e.preventDefault();
            throw new Error(
              'You clicked on link that was not rendered by either <Link> or <LinksContainer>. You should never render a <a> directly. ' +
                "Render the links with @polarnopyret/scope/Link or @polarnopyret/scope/LinksContainer if you're forced to render third party HTML.",
            );
          }
        },
        true,
      );
    }
  }
}

export function isExternalLink(link: HTMLAnchorElement) {
  return link.target === '_blank' || !isInternalUrl(new URLX(link.href));
}

export function isHashLink(link: HTMLAnchorElement) {
  // In IE `link.hostname` equals an empty string if the location is relative
  return (
    (link.hostname === '' || link.hostname === window.location.hostname) &&
    link.pathname === window.location.pathname &&
    link.search === window.location.search &&
    link.hash !== ''
  );
}

export function clickedLink(e: Event) {
  let target: HTMLElement | null = e.target as HTMLElement;
  do {
    if (target.tagName === 'A') {
      return target as HTMLAnchorElement;
    }
    // tslint:disable-next-line no-conditional-assignment
  } while ((target = target.parentElement));
  return null;
}

if (isBrowser()) {
  window.addEventListener('popstate', e => {
    // When we revert a state change, we need to call `back()` which triggers
    // a `popstate` which we want to ignore
    if (ignoreNextPopStateEvent) {
      ignoreNextPopStateEvent = false;
      return;
    }

    // Safari triggers a popstate on load which we want to ignore
    if (!e.state || !e.state.stateId) {
      return;
    }
    const previousState = currentState;
    saveScrollPosition(previousState, scrollPosition());
    const eventState: HistoryState = e.state;
    let eventStateIndex = eventState.stateIndex;
    let event: HistoryEvent | null = null;
    // If we don't have a state index, we assume that it's a pop event.
    // This check is only needed for a short period of time, see comment for getStateIndex().
    if (eventStateIndex === undefined) {
      eventStateIndex = 0;
      event = 'pop';
    } else {
      event = eventStateIndex < currentState.stateIndex ? 'pop' : 'push';
    }
    currentState = eventState;
    currentState.stateIndex = getStateIndex(currentState);
    triggerEvent({
      url: new URLX(typeof eventState.url === 'string' ? eventState.url : convertLegacyUrlToString(eventState.url)),
      event,
      state: eventState,
      scrollPosition: getSavedScrollPosition(eventState),
      previousState,
      options: {},
      revert() {
        const revertToIndex = previousState.stateIndex - eventStateIndex;
        goHistory(revertToIndex, true);

        return () => {
          goHistory(eventStateIndex - previousState.stateIndex);
          return true;
        };
      },
    });
  });

  window.addEventListener('beforeunload', e => {
    saveScrollPosition(currentState, scrollPosition());
  });
}

// Since `stateIndex` didn't always exist on the state object we need to detect that.
// This function can be removed when version 1.3.0 is old and forgotten, because by then
// all visitors state objects will have a stateIndex.
function getStateIndex(state: HistoryState) {
  if (state.stateIndex === undefined) {
    return 0;
  }
  return state.stateIndex;
}

const scrollMemory: { [stateId: string]: number } = __BROWSER__ ? sessionStorage.getSessionStorage('scrollMemory') || {} : {};
function saveScrollPosition(state: HistoryState, position: number) {
  if (!state || !state.stateId || !scrollMemory) {
    if (window.rg4js) {
      window.rg4js('send', {
        error: new Error(
          'state was incorrect in saveScrollPosition: ' +
            JSON.stringify(state) +
            ', scrollMemory: ' +
            JSON.stringify(scrollMemory),
        ),
        tags: ['handled'],
      });
    }
    return;
  }
  scrollMemory[state.stateId] = position;
  sessionStorage.setSesstionStorage('scrollMemory', scrollMemory);
}

function getSavedScrollPosition(state: HistoryState) {
  if (!state || !state.stateId || !scrollMemory) {
    if (window.rg4js) {
      window.rg4js('send', {
        error: new Error(
          'state was incorrect in getSavedScrollPosition: ' +
            JSON.stringify(state) +
            ', scrollMemory: ' +
            JSON.stringify(scrollMemory),
        ),
        tags: ['handled'],
      });
    }
    return undefined;
  }
  if (state.stateId in scrollMemory) {
    return scrollMemory[state.stateId];
  }
  return undefined;
}

function triggerEvent(data: HistoryEventObject, afterEvent = false): Promise<any> {
  const promises: Array<Promise<any>> = [];
  listeners.forEach(o => {
    if (o.event === data.event) {
      const maybePromise = o.callback(data);
      if (maybePromise instanceof Promise) {
        promises.push(maybePromise);
      }
    }
  });
  if (afterEvent) {
    return Promise.all(promises);
  } else {
    return Promise.all(promises).then(
      values => {
        return triggerEvent(Object.assign({}, data, { event: data.event + '-loaded', result: values }), true).then(
          () => values,
          () => values,
        );
      },
      err => {
        return triggerEvent(Object.assign({}, data, { event: data.event + '-failed', error: err }), true).then(
          () => Promise.reject(err),
          () => Promise.reject(err),
        );
      },
    );
  }
}

function stateId() {
  return Math.ceil(Math.random() * 100000000).toString();
}
